"use client";

import { Canvas } from "@react-three/fiber";
import { motion, useTransform } from "motion/react";
import { Suspense, useMemo } from "react";
import { useHeroScroll } from "../../context/HeroScrollContext";
import { useBreakpoints } from "../../hooks";
import AsciiColorGradient from "../three/scene";

/**
 * Fixed, full-viewport ASCII overlay that is visible across INDEX and HERO,
 * then fades out as the next section (post-HERO) begins.
 * - White background with black ASCII glyphs.
 */
export function IndexHeroAsciiOverlay() {
  const { heroProgress } = useHeroScroll();
  // Opacity: fully visible until ~80% through HERO, then fade to 0 by the end.
  // On Mobile: We want it to disappear faster or immediate transition to white?
  // User said "remove the fade... make about section white background".
  // If we make the overlay opacity drop to 0 instantly at the end of hero on mobile, the About section (which is on white bg) will just appear.
  
  const { isMobile } = useBreakpoints();
  
  // Desktop: [0, 0.8, 1] -> [1, 1, 0] (fade out at end)
  // Mobile: [0, 0.9, 1] -> [1, 1, 0] (Keep it visible longer? Or cut it?)
  // If we want "About section white background", we just need to ensure this overlay is GONE by the time About starts.
  // Since About follows Hero, fading out by 1 (end of Hero) works.
  // The user says "remove the fade... make about section white background".
  // Maybe they mean the cross-fade is weird?
  // The overlay IS the black background. If it fades out, we see the white body background underneath.
  // If we want no fade, we can step it?
  
  const opacity = useTransform(
      heroProgress, 
      isMobile ? [0, 0.7, 0.9, 1] : [0, 0.7, 0.9, 1], 
      isMobile ? [1, 1, 0, 0] : [1, 1, 0, 0]
  );

  const gradientProps = useMemo(() => {
    return {
      controls: {
        animSpeed: 0.66,
      },
      asciiControls: {
        asciiEnabled: true,
        asciiInvert: false,
        asciiGamma: .3,
        asciiContrast: .5,
        asciiBrightness: -0.2,
        asciiRangeMin: .05,
        asciiRangeMax: .85,
        asciiActiveCount: 40,
        asciiShift: 8,
        // Swap so glyphs use secondary color (black), bg uses srcColor (white)
        asciiSwapColors: false,
        asciiPrimaryColor: { r: 128, g: 128, b: 128 },
        asciiSecondaryColor: { r: 35, g: 31, b: 32 },
      },
    };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity, backgroundColor: "rgb(35, 31, 32)" }}
      aria-hidden
    >
      <Canvas dpr={[1, 1.5]} gl={{ antialias: true }}>
        <Suspense fallback={null}>
          <AsciiColorGradient
            animSpeed={gradientProps.controls.animSpeed}
            asciiEnabled={gradientProps.asciiControls.asciiEnabled}
            asciiInvert={gradientProps.asciiControls.asciiInvert}
            asciiGamma={gradientProps.asciiControls.asciiGamma}
            asciiContrast={gradientProps.asciiControls.asciiContrast}
            asciiBrightness={gradientProps.asciiControls.asciiBrightness}
            asciiRangeMin={gradientProps.asciiControls.asciiRangeMin}
            asciiRangeMax={gradientProps.asciiControls.asciiRangeMax}
            asciiActiveCount={gradientProps.asciiControls.asciiActiveCount}
            asciiShift={gradientProps.asciiControls.asciiShift}
            asciiPrimaryColor={gradientProps.asciiControls.asciiPrimaryColor}
            asciiSecondaryColor={gradientProps.asciiControls.asciiSecondaryColor}
            asciiSwapColors={gradientProps.asciiControls.asciiSwapColors}
          />
        </Suspense>
      </Canvas>
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent z-10" />
    </motion.div>
  );
}


